import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, SwitchCamera, Images, Camera } from "lucide-react";
import { useFlow } from "@/flowStore";

export default function Capture() {
  const navigate = useNavigate();
  const { setPhoto } = useFlow();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [facing, setFacing] = useState("user");
  const [status, setStatus] = useState("loading"); // loading | ready | denied
  const [busy, setBusy] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const timerRef = useRef(null);

  const clearCountdown = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setCountdown(null);
  }, []);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startStream = useCallback(async (mode) => {
    stopStream();
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus("denied");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setStatus("ready");
    } catch (e) {
      setStatus("denied");
    }
  }, [stopStream]);

  useEffect(() => {
    startStream(facing);
    return () => stopStream();
  }, [facing, startStream, stopStream]);

  const flip = () => {
    setFacing((f) => (f === "user" ? "environment" : "user"));
  };

  const onCapture = () => {
    if (!videoRef.current) return;
    setBusy(true);
    try {
      const video = videoRef.current;
      const canvas = document.createElement("canvas");
      const w = video.videoWidth || 720;
      const h = video.videoHeight || 960;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (facing === "user") {
        ctx.translate(w, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(video, 0, 0, w, h);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.75);
      setPhoto(dataUrl);
      stopStream();
      navigate("/preview");
    } finally {
      setBusy(false);
    }
  };

  // 5-second self-timer: gives people time to get into position.
  const startCountdown = () => {
    if (busy || countdown !== null || !videoRef.current) return;
    setCountdown(5);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          onCapture();
          return null;
        }
        return c - 1;
      });
    }, 1000);
  };

  useEffect(() => () => clearCountdown(), [clearCountdown]);

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPhoto(reader.result);
      stopStream();
      navigate("/preview");
    };
    reader.readAsDataURL(file);
  };

  if (status === "denied") {
    return (
      <div className="screen" data-testid="capture-screen">
        <div className="perm-gate">
          <div className="perm-icon">
            <Camera size={44} />
          </div>
          <h2 className="perm-title">Autorise l&apos;appareil photo</h2>
          <p className="perm-sub">
            Pour capturer ton moment Stammtisch, on a besoin d&apos;accéder à la caméra. Sinon, choisis
            une photo depuis ta galerie.
          </p>
          <button
            className="perm-cta"
            data-testid="grant-camera-button"
            onClick={() => startStream(facing)}
          >
            Réessayer
          </button>
          <button
            className="perm-link"
            data-testid="use-gallery-instead-button"
            onClick={() => fileRef.current?.click()}
          >
            Choisir depuis la galerie
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={onPickFile}
            data-testid="gallery-file-input"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="camera-screen" data-testid="capture-screen">
      <video
        ref={videoRef}
        className={`camera-video ${facing === "environment" ? "no-mirror" : ""}`}
        playsInline
        muted
        autoPlay
      />

      <div className="cam-topbar">
        <button className="cam-icon-btn" data-testid="back-button" onClick={() => { clearCountdown(); stopStream(); navigate(-1); }}>
          <ChevronLeft size={22} />
        </button>
        <div>
          <div className="cam-title-eyebrow">STAMMTISCH CHALLENGE</div>
          <div className="cam-title">Prends la pose</div>
        </div>
        <button className="cam-icon-btn" data-testid="flip-camera-button" onClick={flip}>
          <SwitchCamera size={22} />
        </button>
      </div>

      <div className="cam-frame">
        <div className="corner corner-tl" />
        <div className="corner corner-tr" />
        <div className="corner corner-bl" />
        <div className="corner corner-br" />
      </div>

      {countdown !== null ? (
        <div className="countdown-overlay" data-testid="countdown-overlay">
          <span key={countdown} className="countdown-number">{countdown}</span>
          <span className="countdown-hint">Prépare-toi&nbsp;!</span>
        </div>
      ) : null}

      <div className="cam-bottombar">
        <button className="cam-side-btn" data-testid="gallery-button" onClick={() => fileRef.current?.click()}>
          <Images size={22} />
          <span className="cam-side-label">Galerie</span>
        </button>

        <button
          className="shutter"
          data-testid="shutter-button"
          onClick={startCountdown}
          disabled={busy || countdown !== null}
        >
          <div className="shutter-inner" />
        </button>

        <div style={{ width: 64 }} />
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={onPickFile}
        data-testid="gallery-file-input"
      />
    </div>
  );
}
